import React, { useState, useEffect } from "react";
import axios from "axios";
import { getFileExtensionByMime } from "@/lib/utils";
import kycService from "../../../services/api/kyc.service";

const getAuthToken = () => localStorage.getItem("authToken");

const DocumentViewer = ({ kycId, documentType, documentName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [documentMimeType, setDocumentMimeType] = useState(null);

  // Load document only when kycId or documentType changes
  useEffect(() => {
    const loadDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        const response = await kycService.getKycDocument(
          kycId,
          documentType,
          token
        );

        const contentType = response.headers["content-type"];
        const blob = new Blob([response.data], { type: contentType });
        const url = URL.createObjectURL(blob);

        setDocumentMimeType(contentType);
        setDocumentUrl(url);
      } catch (err) {
        console.error("Error loading document:", err);
        setError("Failed to load document. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();

    return () => {
      if (documentUrl) URL.revokeObjectURL(documentUrl);
    };
  }, [kycId, documentType]);

  const downloadDocument = async () => {
    try {
      const token = getAuthToken();
      const response = await kycService.downloadKycDocument(
        kycId,
        documentType,
        token
      );

      const contentType = response.headers["content-type"];
      const fileExtension = getFileExtensionByMime(contentType);
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${
        documentName || documentType
      }-${kycId}${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading document:", err);
      setError("Failed to download document. Please try again.");
    }
  };


  const isImage = () => documentMimeType?.startsWith("image/");
  const isViewable = () =>
    [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/svg+xml",
      "text/plain",
      "text/html",
    ].includes(documentMimeType);

  const renderViewer = () => {
    if (!documentUrl) return null;
    if (isImage()) {
      return (
        <img
          src={documentUrl}
          alt={documentName}
          className="max-w-full max-h-full object-contain mx-auto"
        />
      );
    }
    if (documentMimeType === "application/pdf") {
      return (
        <iframe
          src={documentUrl}
          title={documentName}
          width="100%"
          height="100%"
          className="border-0 bg-gray-100"
        />
      );
    }
    return (
      <div className="text-center">
        <p>This document type cannot be previewed.</p>
        <button
          onClick={downloadDocument}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded"
        >
          Download to View
        </button>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">{documentName || documentType}</h3>
        <button
          onClick={downloadDocument}
          className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded"
        >
          Download
        </button>
      </div>

      {error && <div className="text-red-600 mb-2">{error}</div>}
      {loading ? (
        <div className="text-center py-10 text-gray-500">
          Loading document...
        </div>
      ) : (
        <div className="border rounded-lg h-[600px] min-h-[400px] relative overflow-hidden">
          {renderViewer()}
          {!isViewable() && (
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-yellow-100 text-sm text-yellow-800 text-center border-t">
              Some document types can only be downloaded
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentViewer;
